// Types + pure helpers for the onboarding-conversion Funnel tab. The shape
// mirrors the dashboard-api `GET /funnel/summary` response (AGG-5).

import type { SnapFunnel } from "../guerilla/guerillaClient";
import type { CustomerFilmOutcomes } from "../incidents/types";
import type { LaunchpadFunnel } from "../launchpad/launchpadClient";
import type { TrackingLinkDailyMetric } from "../types";

export type FunnelStage = {
  key: string;
  label: string;
  reached: number;
  at_stage: number;
  pct_of_cohort: number;
  step_conversion_pct: number | null;
};

export type FunnelLegSummary = {
  cohort_size: number;
  stages: FunnelStage[];
  /**
   * @deprecated Not shown since TRI-56: it equals the first stage's "Stuck
   * here" (`at_stage`). The API still returns it for MCP consumers.
   */
  link_curious?: number;
  errors: {
    /** @deprecated Contact-level outcome retained for older clients. */
    echo_error: number;
    contact_error: number;
  };
  echo_outcomes: {
    attempted_echoes: number;
    delivered_echoes: number;
    failed_echoes: number;
    unrecovered_echoes: number;
    recovered_echoes: number;
    pending_echoes: number;
  };
  recurring: { graduated: number };
  image_count: {
    avg: number | null;
    n: number;
    histogram: Record<string, number>;
  };
  interview_dropoff: {
    entered: number;
    completed: number;
    stuck: number;
    avg_asked: number | null;
    avg_answered: number | null;
    stuck_histogram: Record<string, number>;
  };
};

export type FunnelSummary = FunnelLegSummary & {
  params: {
    cohort_from: string | null;
    cohort_to: string | null;
    leg: string | null;
    continents: string[] | null;
    skill: string;
    acquisition_group?: string | null;
    aggregate_schema_version?: number;
  };
  incomplete_cohort: boolean;
  by_leg: Record<string, FunnelLegSummary>;
  by_continent: Record<string, FunnelLegSummary>;
  by_acquisition_group?: Record<string, FunnelLegSummary>;
  by_acquisition_surface?: Record<string, FunnelLegSummary>;
  closed_conversion: { arrival_to_rated_pct: number };
  /**
   * Customer-level 72-hour outcome for the cohort window (EI-06): customers
   * counted once per week, "without film" when an approval saw no film within
   * 72 hours, "pending" while younger than 72 hours. Absent on older
   * dashboard-api deployments.
   */
  customer_film_outcomes?: CustomerFilmOutcomes | null;
};

export const FUNNEL_LEG_LABELS: Record<string, string> = {
  website: "Website",
  ctwa: "Meta → WhatsApp",
  organic: "Organic",
  snap: "Snap",
  link: "Tracking link",
  launchpad: "Launchpad",
  // Web Create (MYC-015): creations on my.echoflicks.com, no WhatsApp.
  my_web: "My Echoflicks web",
};

export function legLabel(leg: string): string {
  return FUNNEL_LEG_LABELS[leg] ?? leg;
}

// Continent codes the funnel can break down / filter by. All are shown by
// default; the filter is an include-list (deselect to exclude a region).
export const FUNNEL_CONTINENTS: ReadonlyArray<{
  code: string;
  label: string;
  emoji: string;
}> = [
  { code: "AF", label: "Africa", emoji: "🌍" },
  { code: "EU", label: "Europe", emoji: "🌍" },
  { code: "AS", label: "Asia", emoji: "🌏" },
  { code: "NA", label: "North America", emoji: "🌎" },
  { code: "SA", label: "South America", emoji: "🌎" },
  { code: "OC", label: "Oceania", emoji: "🌏" },
  { code: "AN", label: "Antarctica", emoji: "🧊" },
  { code: "001", label: "Non-geographic", emoji: "🛰️" },
  { code: "UNKNOWN", label: "Unknown", emoji: "❓" },
];

const CONTINENT_LABELS: Record<string, string> = Object.fromEntries(
  FUNNEL_CONTINENTS.map((c) => [c.code, c.label]),
);

export function continentLabel(code: string): string {
  return CONTINENT_LABELS[code] ?? code;
}

export function formatInt(value: number | null | undefined): string {
  return (value ?? 0).toLocaleString("en-US");
}

export function formatPct(value: number | null | undefined): string {
  if (value == null) return "—";
  return `${value}%`;
}

export function ratePct(part: number, whole: number): number {
  if (!whole) return 0;
  return Math.round((1000 * part) / whole) / 10;
}

export type TrackingLinkOperationalSummary = {
  human_clicks: number;
  new_contacts: number;
  echoes_started: number;
  echoes_completed: number;
};

/** Sum exact tracking-link activity for the Funnel tab's selected time range. */
export function aggregateTrackingLinkActivity(
  rows: readonly TrackingLinkDailyMetric[],
): TrackingLinkOperationalSummary {
  return rows.reduce<TrackingLinkOperationalSummary>(
    (totals, row) => ({
      human_clicks: totals.human_clicks + (row.short_link_clicks ?? 0),
      new_contacts: totals.new_contacts + (row.new_contacts ?? 0),
      echoes_started: totals.echoes_started + (row.echoes_started ?? 0),
      echoes_completed: totals.echoes_completed + (row.echoes_completed ?? 0),
    }),
    {
      human_clicks: 0,
      new_contacts: 0,
      echoes_started: 0,
      echoes_completed: 0,
    },
  );
}

/** Convert the Funnel API's range token into the operational view's day filter. */
/** Drill-through to the contact list filtered by furthest funnel stage. */
export function stageContactsHref(stageKey: string): string {
  const filter = encodeURIComponent(JSON.stringify({ funnel_stage: stageKey }));
  return `/contacts?filter=${filter}`;
}

// ---------------------------------------------------------------------------
// Snap pre-WhatsApp stage merge (SNAP-SCAN-10)
// ---------------------------------------------------------------------------

export const SNAP_QR_STAGE_KEY = "qr_scanned";

/** A funnel stage row augmented for the merged snap view. */
export type MergedFunnelStage = FunnelStage & {
  /**
   * True for the prepended, pre-contact "QR scanned" bar. It is sourced from
   * the bot's token-keyed funnel (no CONTACT# row), so it must render visually
   * distinct and carry no drill-through link.
   */
  pre_contact?: boolean;
};

export type SnapMergeResult = {
  /** Dashboard stages with the "QR scanned" bar prepended. */
  stages: MergedFunnelStage[];
  /**
   * When the bot's own `conversation_started` count differs from the
   * dashboard's `reached` for that stage, this surfaces the two numbers so the
   * UI can footnote the reconciliation. Null when they agree (or no snap data).
   */
  reconciliation: { qr_side_messaged: number; funnel_messaged: number } | null;
};

/**
 * Prepend the bot's "QR scanned" stage before the dashboard's
 * "conversation_started" bar, anchoring the two independent datasets on
 * messaged == conversation_started.
 *
 * - `qr_scanned.reached` = bot scanned count.
 * - Its `step_conversion_pct` = messaged / scanned (the new top drop), where
 *   "messaged" is anchored on the dashboard's own conversation_started count so
 *   the visible funnel is internally consistent.
 * - `pct_of_cohort` is left at 100 for the prepended bar (it is the new head of
 *   the funnel — the pre-contact cohort denominator).
 *
 * Pure and node-testable: no React, no fetch. When `snap` is null (endpoint
 * errored / degraded) the dashboard stages are returned untouched.
 */
export function mergeSnapPreStage(
  dashboardStages: FunnelStage[],
  snap: SnapFunnel | null | undefined,
): SnapMergeResult {
  if (!snap) {
    return { stages: [...dashboardStages], reconciliation: null };
  }

  const scanned =
    snap.stages.find((s) => s.key === SNAP_QR_STAGE_KEY)?.count ?? 0;
  const botMessaged =
    snap.stages.find((s) => s.key === "conversation_started")?.count ?? 0;

  const dashboardMessaged =
    dashboardStages.find((s) => s.key === "conversation_started")?.reached ?? 0;

  // Anchor the scanned->messaged drop on the dashboard's messaged count so the
  // rendered funnel reads consistently top-to-bottom.
  const stepConversion =
    scanned > 0 ? Math.round((1000 * dashboardMessaged) / scanned) / 10 : null;

  const qrStage: MergedFunnelStage = {
    key: SNAP_QR_STAGE_KEY,
    label: "QR scanned",
    reached: scanned,
    at_stage: Math.max(0, scanned - dashboardMessaged),
    pct_of_cohort: 100,
    step_conversion_pct: null,
    pre_contact: true,
  };

  // The dashboard's first stage now has an inbound step-conversion from scans.
  const merged: MergedFunnelStage[] = dashboardStages.map((s) =>
    s.key === "conversation_started"
      ? { ...s, step_conversion_pct: stepConversion }
      : { ...s },
  );

  const reconciliation =
    botMessaged !== dashboardMessaged
      ? { qr_side_messaged: botMessaged, funnel_messaged: dashboardMessaged }
      : null;

  return { stages: [qrStage, ...merged], reconciliation };
}

// ---------------------------------------------------------------------------
// Launchpad pre-WhatsApp stage merge
// ---------------------------------------------------------------------------

export const LAUNCHPAD_SESSION_STAGE_KEY = "session_started";
export const LAUNCHPAD_MEDIA_READY_STAGE_KEY = "media_ready";

export type LaunchpadMergeResult = {
  /** Dashboard stages with "Launchpad started" + "Uploads finalized" prepended. */
  stages: MergedFunnelStage[];
  /**
   * When the bot's own `conversation_started` count differs from the
   * dashboard's `reached` for that stage, this surfaces the two numbers so the
   * UI can footnote the reconciliation. Null when they agree (or no launchpad
   * data).
   */
  reconciliation: {
    launchpad_side_messaged: number;
    funnel_messaged: number;
  } | null;
};

/**
 * Prepend the bot's two token-keyed pre-WhatsApp stages — "Launchpad started"
 * (`session_started`) and "Uploads finalized" (`media_ready`) — before the
 * dashboard's "conversation_started" bar, anchoring the two independent
 * datasets on messaged == conversation_started (the mergeSnapPreStage
 * pattern, with a two-stage web head instead of one QR bar).
 *
 * - `session_started.reached` = bot session-start count; its
 *   `step_conversion_pct` is null and its `pct_of_cohort` is 100 (it is the
 *   new head of the funnel — the pre-contact cohort denominator).
 * - `media_ready.step_conversion_pct` = media_ready / session_started.
 * - The dashboard's `conversation_started` gets an inbound step-conversion
 *   from `media_ready`, anchored on the dashboard's own conversation_started
 *   count so the visible funnel is internally consistent.
 * - `at_stage` on each pre-contact bar = its count minus the next stage's.
 *
 * Pure and node-testable: no React, no fetch. When `launchpad` is null
 * (endpoint errored / degraded) the dashboard stages are returned untouched.
 */
export function mergeLaunchpadPreStage(
  dashboardStages: FunnelStage[],
  launchpad: LaunchpadFunnel | null | undefined,
): LaunchpadMergeResult {
  if (!launchpad) {
    return { stages: [...dashboardStages], reconciliation: null };
  }

  const started =
    launchpad.stages.find((s) => s.key === LAUNCHPAD_SESSION_STAGE_KEY)
      ?.count ?? 0;
  const mediaReady =
    launchpad.stages.find((s) => s.key === LAUNCHPAD_MEDIA_READY_STAGE_KEY)
      ?.count ?? 0;
  const botMessaged =
    launchpad.stages.find((s) => s.key === "conversation_started")?.count ?? 0;

  const dashboardMessaged =
    dashboardStages.find((s) => s.key === "conversation_started")?.reached ?? 0;

  const sessionStage: MergedFunnelStage = {
    key: LAUNCHPAD_SESSION_STAGE_KEY,
    label: "Launchpad started",
    reached: started,
    at_stage: Math.max(0, started - mediaReady),
    pct_of_cohort: 100,
    step_conversion_pct: null,
    pre_contact: true,
  };

  const mediaReadyStage: MergedFunnelStage = {
    key: LAUNCHPAD_MEDIA_READY_STAGE_KEY,
    label: "Uploads finalized",
    reached: mediaReady,
    at_stage: Math.max(0, mediaReady - dashboardMessaged),
    pct_of_cohort: ratePct(mediaReady, started),
    step_conversion_pct: started > 0 ? ratePct(mediaReady, started) : null,
    pre_contact: true,
  };

  // Anchor the uploads->messaged drop on the dashboard's messaged count so
  // the rendered funnel reads consistently top-to-bottom.
  const stepConversion =
    mediaReady > 0 ? ratePct(dashboardMessaged, mediaReady) : null;

  // The dashboard's first stage now has an inbound step-conversion from the
  // launchpad's finalized uploads.
  const merged: MergedFunnelStage[] = dashboardStages.map((s) =>
    s.key === "conversation_started"
      ? { ...s, step_conversion_pct: stepConversion }
      : { ...s },
  );

  const reconciliation =
    botMessaged !== dashboardMessaged
      ? {
          launchpad_side_messaged: botMessaged,
          funnel_messaged: dashboardMessaged,
        }
      : null;

  return {
    stages: [sessionStage, mediaReadyStage, ...merged],
    reconciliation,
  };
}
